import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import styled from 'styled-components';

import { CalendarItemModel } from 'shared/models';
import { toDate, toRgba } from 'shared/utilities';
import { useObservable } from 'shared/hooks';
import { CalendarItemTypeEnum } from 'shared/enums';
import { UiIcon } from 'shared/uikit-new';

import { CalendarPageCalendarItemPreview } from 'pages/calendar/components/CalendarPageCalendarItemPreview';
import { CALENDAR_PAGE } from 'pages/calendar/constants/CALENDAR_PAGE';
import { Store } from 'pages/calendar/store';

import './styles.scss';

type PropsType = {
    calendarItem: CalendarItemModel;
    style?: React.CSSProperties;
};

const View = styled.div<{ $backgroundColor: string; $hoverBackgroundColor: string }>`
    background-color: ${props => props.$backgroundColor};

    &:hover {
        background-color: ${props => props.$hoverBackgroundColor};
    }
`;

export const CalendarPageDaysCalendarItem = observer(function CalendarPageDaysCalendarItem({ calendarItem, style = {} }: PropsType) {
    const store = useObservable({
        verticalOrientation: 'bottom',
        horizontalOrientation: 'center',
        isOpened: false,
        bounds: null as DOMRect | null,
        now: toDate(),
    });

    const { height } = style;

    return (
        <>
            <div
                className={classnames('calendar-page-days-calendar-item', `calendar-page-days-calendar-item--${calendarItem.typeId}`, {
                    'calendar-page-days-calendar-item--nowrap': !height || +height <= CALENDAR_PAGE.HOUR_HEIGHT * 2,
                    'calendar-page-days-calendar-item--past': toDate(calendarItem.endAt).isBefore(store.now),
                    [`calendar-page-days-calendar-item--task-tracker-task-${calendarItem.taskTrackerTask?.statusId}`]: !!calendarItem.taskTrackerTask?.id,
                })}
                style={{ ...style }}
                onClick={e => {
                    if (calendarItem.type.is(CalendarItemTypeEnum.Task) && calendarItem.taskTrackerTask?.id) {
                        Store.set('taskTrackerTask', { id: calendarItem.taskTrackerTask.id })
                        return;
                    }
                    store.update({
                        bounds: e.currentTarget.getBoundingClientRect(),
                        isOpened: true,
                    });
                    store.set('isOpened', true);
                }}
                onMouseEnter={e => {
                    const bounds = e.currentTarget.getBoundingClientRect();
                    store.set('verticalOrientation', window.innerHeight - bounds.top - bounds.height > 200 ? 'bottom' : 'top');
                    store.set('horizontalOrientation', window.innerWidth - bounds.left + bounds.width > 270 ? 'center' : 'left');
                }}
            >
                <View
                    className='calendar-page-days-calendar-item__inner'
                    $backgroundColor={toRgba(calendarItem.color, 0.15)}
                    $hoverBackgroundColor={toRgba(calendarItem.color, 0.25)}
                >
                    <div className='calendar-page-days-calendar-item__subject'>
                        {calendarItem.type.is(CalendarItemTypeEnum.Task) && (
                            <UiIcon.CheckCircle className={'calendar-page-days-calendar-item__icon'} size={14} color={calendarItem.color} />
                        )}
                        <span>{calendarItem.subject}</span>
                    </div>
                    <div className={'calendar-page-days-calendar-item__time'}>{`${toDate(calendarItem.startAt)
                        .format('HH:mm')} - ${toDate(calendarItem.endAt)
                        .format(
                            'HH:mm'
                        )}`}</div>
                </View>
                <div
                    className={classnames(
                        'calendar-page-days-calendar-item-tooltip',
                        `calendar-page-days-calendar-item-tooltip--${store.verticalOrientation}`,
                        `calendar-page-days-calendar-item-tooltip--${store.horizontalOrientation}`
                    )}
                >
                    <div className='calendar-page-days-calendar-item-tooltip__subject'>{calendarItem.subject}</div>
                    <div className='calendar-page-days-calendar-item-tooltip__time'>
                        {`${toDate(calendarItem.startAt)
                            .format('dd. DD MMM')}, ${toDate(calendarItem.startAt)
                            .format('HH:mm')} - ${toDate(calendarItem.endAt)
                            .format(
                                'HH:mm'
                            )}`}
                    </div>
                    <div className='calendar-page-days-calendar-item-tooltip__organizer'>Постановщик: {calendarItem.organizer.name}</div>
                </div>
            </div>
            <CalendarPageCalendarItemPreview calendarItem={calendarItem} isOpened={store.isOpened} onClose={() => store.set('isOpened', false)} bounds={store.bounds} />
        </>
    );
});
